import { useTranslation } from 'react-i18next';

interface SelectOptionsEmptyProps {
  /**
   * What to say once the query has ANSWERED with nothing: why the list is empty
   * and what fills it (STYLE_GUIDE § Empty States — content contract). Already
   * translated by the caller, because only the caller knows which entity the
   * options are.
   */
  emptyMessage: string;
  /** The options query has answered (`isSuccess`). */
  loaded: boolean;
  /** The options query could not answer (`isError`). */
  failed?: boolean;
}

/**
 * The row a `<SelectContent>` renders INSTEAD of nothing when its option list is
 * empty.
 *
 * A Radix `<Select>` whose items array is empty opens as a blank box: no rows, no
 * reason, and no next step — the dead end WI 187df059 was reported as ("the list
 * shows no bank accounts"). A picker's result list owes an empty state like any
 * other surface that can render zero items, and zero options is three states with
 * three copies, not one: still answering, could not answer, and
 * answered-with-nothing. Reporting a failed load as "there are none" tells the
 * user their own data does not exist. A restoring persisted cache reads as "still
 * answering" here — the query is `isSuccess: false` until it lands, which is the
 * branch that already says Loading.
 *
 * Deliberately not selectable and not a `<SelectItem>`: it carries no value, and a
 * Radix item with an empty value throws. Keyboard users reach it as text in the
 * open listbox rather than as a focusable option they cannot choose.
 */
export function SelectOptionsEmpty({ emptyMessage, loaded, failed }: SelectOptionsEmptyProps) {
  const { t } = useTranslation('common');

  return (
    <p
      className="px-2 py-3 text-sm text-muted-foreground"
      data-testid="select-options-empty"
    >
      {failed
        ? t('offline.entityLoadFailedMessage')
        : loaded
          ? emptyMessage
          : t('app.loading')}
    </p>
  );
}
